import { Deserializer } from "jsonapi-serializer";
import { CREATED, NO_CONTENT, OK } from "../constants/response-status-codes";
import { SECURITY_POLICY_ID_FIELDS } from "../constants/security-policy-constants";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { Translator } from "../contexts/translator";
import { CertificateDetailsType, CertificateType } from "../models/CertificateType";
import { genericError } from "../models/GenericError";
import { ListQuery } from "../models/ListQuery";
import { Name } from "../models/Name";
import { PaginationModel } from "../models/Pagination";
import { SecurityPolicies, SecurityPoliciesWithSettingData, SecurityPoliciesWrapper } from "../models/SecurityPolicy";
import { StoreEntityResult } from "../models/services/StoreEntityResult";
import { convertDateToHumanDate } from "../utilities/DateUtils";
import { validatePostAndPatchResponse } from "../utilities/requestResponseValidator";
import { APIConnector } from "./api/APIConnector";
import { createPagination } from "./utilities/paginationBuilder";
import { getConstraintViolation, processServerError } from "./utilities/ServiceError";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";

const apiConnector = new APIConnector();

const buildCertificateType = (certificate, translate: Translator): CertificateType => {
    const revoked = certificate["revoked"] === 0 ? translate.byKey(TranslationKeys.notRevoked) : translate.byKey(TranslationKeys.revoked);
    let status = certificate["has-certificate-expired"] === true ? "0" : "1"

    if (certificate["revoked"] === 1) {
        status = "0"
    }

    return {
        id: certificate["id"],
        serialNumber: certificate["serial-number"],
        name: certificate["name"],
        createdBy: getUserAndDate(certificate),
        lastModifiedBy: getUserAndDate(certificate),
        expiryDate: certificate["cert-expiration-date-utc"].replace("T", " ").replace(".000", "").replaceAll("-", "/"),
        certificateAuthority: certificate["issuer-common-name"],
        status: status,
        certStatus: certificate["ks-certificate-status"]["trn-name"],
        certType: certificate["ks-certificate-type"]["trn-name"],
        siteId: certificate["site"]["id"],
        site: certificate["site"]["site-name"],
        revoke: revoked
    };
}

/**
 * Fetches the ID of the default password security policy profile.
 * Returns null if no default policy is found.
 */
export const getDefaultSecurityPolicyId = async (): Promise<string | null> => {
    try {
        const url = `securityPolicyProfiles?filter=isDefault==true&fields[securityPolicyProfiles]=isDefault&page[size]=1`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return await new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: json.errors[0].detail });
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                if (!data || data.length === 0) {
                    return resolve(null);
                }

                resolve(data[0]["id"] ?? null);
            });
        });
    } catch (error) {
        processServerError(error);
        return null;
    }
};

export const getSecurityPolicies = async (listParameters?: ListQuery): Promise<SecurityPoliciesWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const securityPolicyProfilesFields = 'fields[securityPolicyProfiles]=trnName,profileSubType,profileType,site,isDefault';
        const profileSubTypeFields = 'fields[profileSubType]=trnName'
        const profileTypeFields = 'fields[profileType]=trnName';
        const siteFields = 'fields[site]=siteName';
        const url = `securityPolicyProfiles?include=profileSubType,profileType,site&${securityPolicyProfilesFields}&${profileSubTypeFields}&${profileTypeFields}&${siteFields}${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: json.errors[0].detail });
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }
                const securityPolicyProfiles: SecurityPolicies[] = data.map((securityPolicy): SecurityPolicies => {
                    return {
                        id: securityPolicy["id"],
                        name: securityPolicy["trn-name"],
                        profileType: securityPolicy["profile-type"]["trn-name"],
                        profileTypeId: securityPolicy["profile-type"]["id"],
                        profileSubType: securityPolicy["profile-sub-type"]["trn-name"],
                        profileSubTypeId: securityPolicy["profile-sub-type"]["id"],
                        displayName: securityPolicy[""],
                        site: securityPolicy["site"]["site-name"],
                        siteId: securityPolicy["site"]["id"],
                        isDefault: securityPolicy["is-default"],
                    };
                });

                return resolve({ rows: securityPolicyProfiles, pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getSecurityPolicyById = async (securityPolicyId: string, translate: Translator): Promise<SecurityPoliciesWithSettingData> => {
    try {
        const include = 'include=profile,profile.profileType,profile.profileSubType,profile.site,settingDatas.element,settingDatas';
        const settingFields = 'fields[setting]=profile,settingDatas';
        const profileFields = 'fields[profile]=trnName,profileType,profileSubType,profileAlias,site,isDefault,pDesc';
        const profileTypeFields = 'fields[profileType]=trnName';
        const profileSubTypeFields = 'fields[profileSubType]=trnName';
        const settingDatasFields = 'fields[settingData]=element,sdValstring';
        const elementFields = 'fields[element]=eSysname';
        const siteFields = 'fields[site]=siteName';
        const url = `setting?${include}&page[totals]=&page[size]=29&filter=profile.id==${securityPolicyId}&sort=settingType.stOrdinal&${settingFields}&${profileFields}&${profileTypeFields}&${profileSubTypeFields}&${settingDatasFields}&${siteFields}&${elementFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: json.errors[0].detail });
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    reject({ status: response.status, message: genericError });
                }
                if (data && data.length > 0) {
                    const securityPolicy: SecurityPoliciesWithSettingData = {
                        id: data[0].profile.id,
                        name: data[0].profile["trn-name"],
                        profileType: data[0].profile["profile-type"] ? data[0].profile["profile-type"]["trn-name"] : "",
                        profileTypeId: data[0].profile["profile-type"] ? data[0].profile["profile-type"]["id"] : 0,
                        profileSubType: data[0]["profile"]["profile-sub-type"] ? data[0].profile["profile-sub-type"]["trn-name"] : "",
                        profileSubTypeId: data[0]["profile"]["profile-sub-type"] ? data[0].profile["profile-sub-type"]["id"] : 0,
                        displayName: data[0].profile["profile-alias"] || "",
                        site: data[0].profile['site']?.['site-name'] ?? "",
                        siteId: data[0].profile['site']['id'],
                        isDefault: data[0].profile["is-default"] || false,
                        description:data[0].profile["p-desc"],
                        enforceUppercase: false,
                        enforceLowercase: false,
                        enforceDigits: false,
                        enforceSpecialCharacters: false,
                        enforceInitialPasswordChange: false,
                        enforcePasswordLifetime: false,
                        passwordLifetime: 0,
                        enforcePasswordMinLength: false,
                        passwordMinLength: 0,
                        enforcePasswordHistory: false,
                        passwordHistoryNumber: 0,
                        settingDatasRef: [],
                    };

                    // Populate the fields based on the setting data
                    data.forEach((setting: any) => {
                        if (setting["setting-datas"] && setting["setting-datas"].length > 0) {
                            const settingData = setting["setting-datas"][0];
                            const element = settingData["element"];

                            if (element) {
                                const settingDataRef = { id: settingData["id"], sdValstring: settingData["sd-valstring"], elementSysname: element["e-sysname"] };
                                securityPolicy.settingDatasRef.push(settingDataRef);

                                switch (element["e-sysname"]) {
                                    case SECURITY_POLICY_ID_FIELDS.SEC_POLICY_ENFORCE_UPPERCASE:
                                        securityPolicy.enforceUppercase = settingData["sd-valstring"] === "1";
                                        break;
                                    case SECURITY_POLICY_ID_FIELDS.SEC_POLICY_ENFORCE_LOWERCASE:
                                        securityPolicy.enforceLowercase = settingData["sd-valstring"] === "1";
                                        break;
                                    case SECURITY_POLICY_ID_FIELDS.SEC_POLICY_ENFORCE_DIGITS:
                                        securityPolicy.enforceDigits = settingData["sd-valstring"] === "1";
                                        break;
                                    case SECURITY_POLICY_ID_FIELDS.SEC_POLICY_ENFORCE_SPECIAL_CHAR:
                                        securityPolicy.enforceSpecialCharacters = settingData["sd-valstring"] === "1";
                                        break;
                                    case SECURITY_POLICY_ID_FIELDS.SEC_POLICY_ENFORCE_INITIAL_PWD_CHANGE:
                                        securityPolicy.enforceInitialPasswordChange = settingData["sd-valstring"] === "1";
                                        break;
                                    case SECURITY_POLICY_ID_FIELDS.SEC_POLICY_ENFORCE_PWD_LIFETIME:
                                        securityPolicy.enforcePasswordLifetime = settingData["sd-valstring"] === "1";
                                        break;
                                    case SECURITY_POLICY_ID_FIELDS.SEC_POLICY_PWD_LIFETIME_MONTHS:
                                        securityPolicy.passwordLifetime = parseInt(settingData["sd-valstring"]) || 0;
                                        break;
                                    case SECURITY_POLICY_ID_FIELDS.SEC_POLICY_ENFORCE_MIN_LENGTH:
                                        securityPolicy.enforcePasswordMinLength = settingData["sd-valstring"] === "1";
                                        break;
                                    case SECURITY_POLICY_ID_FIELDS.SEC_POLICY_MIN_LENGTH:
                                        securityPolicy.passwordMinLength = parseInt(settingData["sd-valstring"]) || 0;
                                        break;
                                    case SECURITY_POLICY_ID_FIELDS.SEC_POLICY_ENFORCE_PWD_HISTORY:
                                        securityPolicy.enforcePasswordHistory = settingData["sd-valstring"] === "1";
                                        break;
                                    case SECURITY_POLICY_ID_FIELDS.SEC_POLICY_PWD_HISTORY_TOTAL:
                                        securityPolicy.passwordHistoryNumber = parseInt(settingData["sd-valstring"]) || 0;
                                        break;
                                }
                            }
                        }
                    });

                    return resolve(securityPolicy);
                } else {
                    reject({ status: response.status, message: translate.byKey(TranslationKeys.enforceMinimumLength) });
                }
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => { reject(processServerError(error)); });
    }
};

export const getSecurityPoliciesWithPagination = async (listQuery?: ListQuery): Promise<{ rows: Name[]; pagination: PaginationModel }> => {
    try {
        const securityPolicyProfilesFields = 'fields[securityPolicyProfiles]=trnName';
        const filter: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filter);
        const url = `securityPolicyProfiles?${paginationString}&${securityPolicyProfilesFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: json.errors[0].detail });
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    reject({ status: response.status, message: genericError });
                }

                const securityPolicy: Name[] = data.map((securityPolicy) => {
                    return { label: securityPolicy["trn-name"], value: securityPolicy["id"] };
                });
                return resolve({ rows: securityPolicy, pagination: newPagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getSecurityPolicyNames = async (listParameters?: ListQuery): Promise<{ rows: Name[]; pagination: PaginationModel }> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const url = `securityPolicyProfiles?${partialURL}&include=site`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: json.errors[0].detail });
            }

            new Deserializer().deserialize(json, async (error: any, data: any[]) => {
                    if (error) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const securityPolicy: Name[] = data.map(
                        (securityPolicy): Name => {
                            return {
                                value: securityPolicy["id"],
                                label: securityPolicy["trn-name"],
                                optionalColumns: [{ key: "site", value: securityPolicy["site"]?.["site-name"] }],
                            };
                        },
                    );
                    return resolve({ rows: securityPolicy, pagination });
                },
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

/**
 * A list representing the number of passwords remembered.
 *
 * Typical range in practice:
 * - Minimum: 3-5 previous passwords remembered (common in smaller or less regulated environments)
 * - Standard: 10-12 previous passwords remembered (balances security and usability)
 * - High Security: 24 previous passwords remembered (recommended for enterprise or sensitive systems)
 *
 * For our first release we will limit to 12 passwords remembered, maximum.
 *
 * @returns
 */
export const getPasswordRemembered = () => {
    const allOptions = [
        { value: 1, label: "1", isDefault: false },
        { value: 2, label: "2", isDefault: false },
        { value: 3, label: "3", isDefault: false },
        { value: 4, label: "4", isDefault: false },
        { value: 5, label: "5", isDefault: false },
        { value: 6, label: "6", isDefault: false },
        { value: 7, label: "7", isDefault: false },
        { value: 8, label: "8", isDefault: false },
        { value: 9, label: "9", isDefault: false },
        { value: 10, label: "10", isDefault: false },
        { value: 11, label: "11", isDefault: false },
        { value: 12, label: "12", isDefault: false },
    ];

    return (listParameter?: ListQuery): Promise<{ rows: any[]; pagination: PaginationModel }> =>
        new Promise((resolve) => {
            let filteredRows = allOptions;

            if (listParameter?.searchTerm?.value) {
                const searchValue = listParameter.searchTerm.value.toLowerCase();
                filteredRows = allOptions.filter((option) => option.label.toLowerCase().includes(searchValue));
            }

            const pagination: PaginationModel = {
                totalCount: filteredRows.length,
                maxPageSize: listParameter?.pageSize || filteredRows.length,
                totalPages: 1,
                pageNumber: 1,
            };

            resolve({ rows: filteredRows, pagination });
        }
    );
};

export const getPasswordMonths = () => {
    const allOptions = [
        { value: 1, label: "1", isDefault: false },
        { value: 2, label: "2", isDefault: false },
        { value: 3, label: "3", isDefault: false },
        { value: 4, label: "4", isDefault: false },
        { value: 5, label: "5", isDefault: false },
        { value: 6, label: "6", isDefault: false },
        { value: 7, label: "7", isDefault: false },
        { value: 8, label: "8", isDefault: false },
        { value: 9, label: "9", isDefault: false },
        { value: 10, label: "10", isDefault: false },
        { value: 11, label: "11", isDefault: false },
        { value: 12, label: "12", isDefault: false },
    ];

    return (listParameter?: ListQuery): Promise<{ rows: any[]; pagination: PaginationModel }> =>
        new Promise((resolve) => {
            let filteredRows = allOptions;

            if (listParameter?.searchTerm?.value) {
                const searchValue = listParameter.searchTerm.value.toLowerCase();
                filteredRows = allOptions.filter((option) => option.label.toLowerCase().includes(searchValue));
            }

            const pagination: PaginationModel = {
                totalCount: filteredRows.length,
                maxPageSize: listParameter?.pageSize || filteredRows.length,
                totalPages: 1,
                pageNumber: 1,
            };

            resolve({ rows: filteredRows, pagination });
        }
    );
};

export const storeSecurityPolicy = async (policy: any, createNewSecurityProfile = true) => {
    try {
        const newRecord = {
            data: {
                type: "profile",
                id: policy.id,
                attributes: {
                    pAlias: null,
                    pDesc: policy.description,
                    pHidden: 0,
                    nameOriginal: policy.name,
                    pSysname: "USER_DEFINED",
                    pSysnameType: "",
                    inheritsFromId: "77",
                },
                relationships: {
                    profileType: {
                        data: { type: "profileType", id: "13" },
                    },
                    profileSubType: {
                        data: { type: "profileSubType", id: policy.subType },
                    },
                    site: {
                        data: { type: "site", id: policy.site },
                    },
                },
            },
        };
        const url = `profile${createNewSecurityProfile ? "" : `/${policy.id}`}`;
        let response;
        await apiConnector.sendData(url, createNewSecurityProfile, newRecord)
            .then((data) => { response = data; })
            .catch((error) => {
                return new Promise((resolve, reject) => {
                    return reject(processServerError(error));
                });
            });

        return validatePostAndPatchResponse(response, newRecord.data.id);
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const deleteSecurityPolicy = async (id: string): Promise<{ id: string; success: boolean }> => {
    try {
        const url = `profile/${id}`;
        const securityPolicy = { data: { type: "profile", id: id } };
        const response = await apiConnector.deleteData(url, securityPolicy);
        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true });
            }

            const json = await response.json();

            return reject({
                status: response.status,
                id: id,
                success: false,
                message: json.errors[0].detail,
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const createSecurityPolicy = async (data: any): Promise<StoreEntityResult> => {
    let response: Response;

    await apiConnector.sendData("", true, data, true)
        .then((data) => { response = data; })
        .catch((error) => {
            return new Promise((resolve, reject) => {
                return reject(processServerError(error));
            });
        });

    return new Promise<StoreEntityResult>(async (resolve, reject) => {
        if (response.status === CREATED || response.status === OK) {
            return resolve({ id: 1, success: true });
        }

        const content = await response.json();
        return reject({
            status: response.status,
            id: 1,
            success: false,
            message: content.errors.map((error) => error.detail),
        });
    });
};

export const saveSecurityPolicyService = async (values: any): Promise<StoreEntityResult> => {
    try {
        const data = {
            type: "securityPolicy",
            attributes: {
                id: "0", // This is given 0 since we are creating a new instance.
                name: values.name,
                description: values.description ? values.description : "",
                site: values.siteId,
            },
            relationships: {
                settingData: [
                    {
                        eSysnameLookup: SECURITY_POLICY_ID_FIELDS.SEC_POLICY_ENFORCE_UPPERCASE,
                        sdValstring: values.enforceUppercase ? "1" : "0" ,
                    },
                    {
                        eSysnameLookup: SECURITY_POLICY_ID_FIELDS.SEC_POLICY_ENFORCE_DIGITS,
                        sdValstring: values.enforceDigits ? "1" : "0",
                    },
                    {
                        eSysnameLookup: SECURITY_POLICY_ID_FIELDS.SEC_POLICY_ENFORCE_PWD_LIFETIME,
                        sdValstring: values.enforcePasswordLifetime ? "1" : "0",
                    },
                    {
                        eSysnameLookup: SECURITY_POLICY_ID_FIELDS.SEC_POLICY_ENFORCE_MIN_LENGTH,
                        sdValstring: values.enforcePasswordMinLength ? "1" : "0",
                    },
                    {
                        eSysnameLookup: SECURITY_POLICY_ID_FIELDS.SEC_POLICY_ENFORCE_PWD_HISTORY,
                        sdValstring: values.enforcePasswordHistory ? "1" : "0",
                    },
                    {
                        eSysnameLookup: SECURITY_POLICY_ID_FIELDS.SEC_POLICY_ENFORCE_LOWERCASE,
                        sdValstring: values.enforceLowercase ? "1" : "0",
                    },
                    {
                        eSysnameLookup: SECURITY_POLICY_ID_FIELDS.SEC_POLICY_ENFORCE_SPECIAL_CHAR,
                        sdValstring: values.enforceSpecialCharacters ? "1" : "0",
                    },
                    {
                        eSysnameLookup: SECURITY_POLICY_ID_FIELDS.SEC_POLICY_ENFORCE_INITIAL_PWD_CHANGE,
                        sdValstring: values.enforceInitialPasswordChange ? "1" : "0",
                    },
                    {
                        eSysnameLookup: SECURITY_POLICY_ID_FIELDS.SEC_POLICY_PWD_LIFETIME_MONTHS,
                        sdValstring: values.passwordLifetime,
                    },
                    {
                        eSysnameLookup: SECURITY_POLICY_ID_FIELDS.SEC_POLICY_MIN_LENGTH,
                        sdValstring: values.passwordMinLength,
                    },
                    {
                        eSysnameLookup: SECURITY_POLICY_ID_FIELDS.SEC_POLICY_PWD_HISTORY_TOTAL,
                        sdValstring: values.passwordHistoryNumber,
                    },
                ],
            },
        };
        return createSecurityPolicy(data);
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const securityPolicyUserLogin = async (securityPolicyId: string, userLoginIds: string[]) => {
    const adjustRecord = {
        data: {
            type: "securityPolicyUserLogin",
            id: securityPolicyId,
            attributes: { securityPolicyId: securityPolicyId, userLoginIds: userLoginIds, },
        },
    };

    const url = `securityPolicyUserLogin`;
    let response;

    await apiConnector.sendData(url, true, adjustRecord)
        .then((data) => { response = data; })
        .catch((error) => {
            return new Promise((resolve, reject) => {
                reject(processServerError(error));
            });
        });

    return new Promise(async (resolve, reject) => {
        if (response) {
            const content = await response.json();
            if (content.errors) {
                return reject({ status: response.status, message: content.errors[0].detail });
            }

            new Deserializer().deserialize(content, async (error, securityResponse) => {
                return resolve({
                    userLoginId: securityResponse["user-login-id"],
                    sevenDaysToExpire: securityResponse["seven-days-till-password-has-expired"],
                    passwordExpired: securityResponse["password-expired"],
                    oneDayToExpire: securityResponse["one-day-till-password-has-expired"],
                    oneMonthToExpire: securityResponse["less-than-one-month-till-password-expired"],
                    passwordIsGood: securityResponse["password-is-good"],
                    firstLogin: securityResponse["first-login"],
                    failsRequirementCheck: securityResponse["fails-requirement-check"],
                });
            });
            return;
        }

        return reject({ status: response.status, success: false, message: genericError });
    });
};

const getUserAndDate = (certificate: any) => {
    const userName: string = certificate["user-login"]?.["ul-username"] || "";
    const dates: string = certificate["cert-created-date-utc"].replace("T", " ").replace(".000", "").replaceAll("-", "/");

    return `${userName ? userName : "system"} (${dates})`;
}

export const getControllerCertificates = async (translate: Translator, listParameters?: ListQuery): Promise<{ rows: CertificateType[]; pagination: PaginationModel }> => {
    try {
        let filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const filterController = "deviceImprox.id>0"
        filter = filter ? `${filter};${filterController}` : `&filter=${filterController}`;
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const certificateField = 'fields[ksCertificate]=name,serialNumber,certCreatedDateUtc,certExpirationDateUtc,issuerCommonName,hasCertificateExpired,userLogin,deviceImprox,ksCertificateStatus,site,revoked';
        const userLoginField = 'fields[userLogin]=ulUsername';
        const deviceImproxField = 'fields[controller]=devName';
        const ksCertificateStatusField = 'fields[ksCertificateStatus]=trnName';
        const ksCertificateTypeField = 'fields[ksCertificateType]=trnName';
        const siteFields = 'fields[site]=siteName';
        const url = `ksCertificate?include=site,userLogin,deviceImprox,ksCertificateStatus,ksCertificateType&${certificateField}&${ksCertificateStatusField}&${siteFields}&${userLoginField}&${deviceImproxField}&${ksCertificateTypeField}${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: json.errors[0].detail });
            }
            new Deserializer().deserialize(json, async (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }
                const certificates: CertificateType[] = data.map((certificate): CertificateType => {
                    return buildCertificateType(certificate, translate);
                });
                return resolve({ rows: certificates, pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getCertificatesWithPagination = async (translate: Translator, listParameters?: ListQuery, certificateTypeId?: number): Promise<{ rows: CertificateType[]; pagination: PaginationModel }> => {
    try {
        let filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);

        // Filter by certificate type if provided
        const filterControllerType = (certificateTypeId) ? "ksCertificateType.id=="+certificateTypeId : null;
        if (certificateTypeId) {
            filter = filter ? `${filter};${filterControllerType}` : `&filter=${filterControllerType}`;
        }

        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const certificateField = 'fields[ksCertificate]=name,serialNumber,certCreatedDateUtc,certExpirationDateUtc,issuerCommonName,hasCertificateExpired,userLogin,ksCertificateStatus,ksCertificateType,site,revoked';
        const userLoginField = 'fields[userLogin]=ulUsername';
        const ksCertificateStatusField = 'fields[ksCertificateStatus]=trnName';
        const ksCertificateTypeField = 'fields[ksCertificateType]=trnName';
        const siteFields = 'fields[site]=siteName';
        const include = 'include=site,userLogin,ksCertificateStatus,ksCertificateType';
        const url = `ksCertificate?${include}&${certificateField}&${siteFields}&${ksCertificateStatusField}&${userLoginField}&${ksCertificateTypeField}${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: json.errors[0].detail });
            }
            new Deserializer().deserialize(json, async (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }
                const certificates: CertificateType[] = data.map((certificate): CertificateType => {
                    return buildCertificateType(certificate, translate);
                });
                return resolve({ rows: certificates, pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getWebserverCertificatesWithPagination = async (translate: Translator, listParameters?: ListQuery): Promise<{ rows: CertificateType[]; pagination: PaginationModel }> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const certificateField = 'fields[ksCertificate]=name,serialNumber,certCreatedDateUtc,certExpirationDateUtc,issuerCommonName,hasCertificateExpired,userLogin,ksCertificateStatus,ksCertificateType,site';
        const userLoginField = 'fields[userLogin]=ulUsername';
        const ksCertificateStatusField = 'fields[ksCertificateStatus]=trnName';
        const ksCertificateTypeField = 'fields[ksCertificateType]=trnName';
        const siteFields = 'fields[site]=siteName';
        const include = 'include=site,userLogin,ksCertificateStatus,ksCertificateType';
        const url = `ksCertificate?${include}&${certificateField}&${siteFields}&${ksCertificateStatusField}&${userLoginField}&${ksCertificateTypeField}${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: json.errors[0].detail });
            }
            new Deserializer().deserialize(json, async (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }
                const certificates: CertificateType[] = data.map((certificate): CertificateType => {
                    return buildCertificateType(certificate, translate);
                });
                return resolve({ rows: certificates, pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getCertificateNamesWithPagination = async (listQuery?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filters);
        const include = 'include=site,deviceImprox,ksCertificateType,ksCertificateStatus';
        const ksCertificateFields = 'fields[ksCertificate]=name,site,deviceImprox,ksCertificateType,ksCertificateStatus';
        const siteFields = 'fields[site]=siteName';
        const url = `ksCertificate?&${include}&${ksCertificateFields}&${siteFields}&${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, items: any[]) => {
                if (err) {
                    return reject({status: response.status, message: genericError });
                }

                const certificateNames = items.filter((item) => {
                   if (item["device-improx"] == null) {
                       // Shows that we haven't used this cert yet.
                       return item;
                   }
                });

                const certificates: Name[] = certificateNames.map((certificate): Name => {
                    return {
                        value: certificate['id'],
                        label: certificate['name']
                    };
                });
                return resolve({ rows: certificates, pagination: newPagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getCertificateDetailsById = async (certificateId: number, translate: Translator): Promise<{ data: CertificateDetailsType }> => {
    try {
        const include = 'include=site,ksCertificateType';
        const siteField = 'fields[site]=siteName';
        const typeField = 'fields[ksCertificateType]=trnName';
        let certificateField = 'fields[ksCertificate]=name,serialNumber,commonName,certCreatedDateUtc,certExpirationDateUtc,issuerCommonName,issuerOrganization,site,';
        certificateField += 'issuerOrganizationalUnit,organisation,organizationalUnit,publicKeyFingerprint,certificateAlgorithmType,subjectAlternativeName,encryptionType,'
        certificateField += 'keySize,curve,shaAlgorithm,caCertificateId,certificateAuthority,ksCertificateType,revoked';
        const url = `ksCertificate/${certificateId}?${include}&${siteField}&${typeField}&${certificateField}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: json.errors[0].detail });
            }
            new Deserializer().deserialize(json, async (error: any, data: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const certificateDetails: CertificateDetailsType = {
                    id: data["id"],
                    serialNumber: data["serial-number"] || "<" + translate.byKeyFormatted(TranslationKeys.notPartOfcertificate) + ">",
                    name: data["name"] || "<" + translate.byKeyFormatted(TranslationKeys.notPartOfcertificate) + ">",
                    commonName: data["common-name"] || "<" + translate.byKeyFormatted(TranslationKeys.notPartOfcertificate) + ">",
                    issuedOn: convertDateToHumanDate(data["cert-created-date-utc"]) || "<" + translate.byKeyFormatted(TranslationKeys.notPartOfcertificate) + ">",
                    expiresOn: convertDateToHumanDate(data["cert-expiration-date-utc"]) || "<" + translate.byKeyFormatted(TranslationKeys.notPartOfcertificate) + ">",
                    issuerCommonName: data["issuer-common-name"] || "<" + translate.byKeyFormatted(TranslationKeys.notPartOfcertificate) + ">",
                    issuerOrganisation: data["issuer-organisation"] || "<" + translate.byKeyFormatted(TranslationKeys.notPartOfcertificate) + ">",
                    issuerOrganisationalUnit: data["issuer-organisational-unit"] || "<" + translate.byKeyFormatted(TranslationKeys.notPartOfcertificate) + ">",
                    organisation: data["organisation"] || "<" + translate.byKeyFormatted(TranslationKeys.notPartOfcertificate) + ">",
                    organisationalUnit: data["organisational-unit"] || "<" + translate.byKeyFormatted(TranslationKeys.notPartOfcertificate) + ">",
                    publicKeyFingerprintAlgorithm: data["public-key-fingerprint"]["algorithm"] || "<" + translate.byKeyFormatted(TranslationKeys.notPartOfcertificate) + ">",
                    publicKeyFingerprintParams: data["public-key-fingerprint"]["params"] || "<" + translate.byKeyFormatted(TranslationKeys.notPartOfcertificate) + ">",
                    publicKeyFingerprintModulus: data["public-key-fingerprint"]["modulus"] || "<" + translate.byKeyFormatted(TranslationKeys.notPartOfcertificate) + ">",
                    publicKeyFingerprintExponent: data["public-key-fingerprint"]["exponent"] || "<" + translate.byKeyFormatted(TranslationKeys.notPartOfcertificate) + ">",
                    certificateFingerprintAlgorithm: data["certificate-algorithm-type"] || "<" + translate.byKeyFormatted(TranslationKeys.notPartOfcertificate) + ">",
                    certificateFingerprintModulus: data["certificate-fingerprint-modulus"] || "<" + translate.byKeyFormatted(TranslationKeys.notPartOfcertificate) + ">",
                    subjectAlternativeName: data["subject-alternative-name"] || "<" + translate.byKeyFormatted(TranslationKeys.notPartOfcertificate) + ">",
                    encryptionType: data["encryption-type"],
                    shaAlgorithm: data["sha-algorithm"],
                    curve: data["curve"],
                    keySize: data["key-size"],
                    caCertificateId: data["ca-certificate-id"],
                    isCaCertificate: data["certificate-authority"],
                    certificateTypeId: data["ks-certificate-type"]["id"] || 1,
                    certificateTypeName: data["ks-certificate-type"]["trn-name"] || "",
                    isRevoked: data["revoked"] === 0 ? false : true
                };
                return resolve({ data: certificateDetails });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const generateCertificate = async (data: any): Promise<any> => {
    try {
        const newRecord = {
            data: {
                type: "ksCertificate",
                id: data.id,
                attributes: {
                    name: data.alias,
                    subjectAlternativeName: data.subjectAlternativeName,
                    validityIn: data.validityIn,
                    validFor: data.validFor,
                    commonName: data.commonName,
                    encryptionType: data.encryptionType,
                    keySize: data.keySize,
                    curve: data.curve,
                    shaAlgorithm: data.shaAlgorithm,
                    certificateAuthority: data.certificateAuthority,
                    caCertificateId: data.caCertificateId,
                    revoked: data.revoked,
                },
                relationships: {
                    site: {
                        data: { type: "site", id: data.siteId },
                    },
                    userLogin: {
                        data: { type: "userLogin", id: data.userId },
                    },
                    ksCertificateType: {
                        data: { type: "ksCertificateType", id: data.certificateTypeId },
                    }
                },
            },
        };

        const createCert = !newRecord.data.id;
        const url = "ksCertificate" + (createCert ? "" : `/${data.id}`);
        let response: Response;
        await apiConnector.sendData(url, createCert, newRecord)
            .then((data) => { response = data; })
            .catch((error) => {
                return new Promise((resolve, reject) => {
                    return reject(processServerError(error));
                });
            });

        return new Promise(async (resolve, reject) => {
            if (response) {
                if (createCert) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({
                            status: response.status,
                            message: content.errors[0].detail,
                        });
                    }
                    return resolve({ data: content });
                } else if (response.status === 204) {
                    return resolve({ id: 0, success: true });
                }
                const errorContent = await response.json();
                if (errorContent.errors) {
                    return reject({ status: response.status, success: false, message: errorContent.errors[0].detail || genericError });
                }
                return reject({ status: response.status, success: false, message: genericError });
            }
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const deleteCertificate = async (id: string): Promise<{ id: string, success: boolean }> => {
    try {
        const record = {
            data: {
                type: "ksCertificate",
                id: id
            }
        }
        const url = `ksCertificate/${id}`;
        const response = await apiConnector.deleteData(url, record);
        return new Promise(async (resolve, reject) => {
            if (response.ok === false) {
                try {
                    const json = await response.json();
                    return reject({status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail)});
                } catch (error) {
                    return reject({status: response.status, id: id, success: false, message: genericError});
                }
            }
            if (response.status === NO_CONTENT) {
                return resolve({id: id, success: true})
            }
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getCertificateTypes = async (listQuery?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filters);
        const url = `ksCertificateType?fields[ksCertificateType]=trnName&${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, items: any[]) => {
                if (err) {
                    return reject({status: response.status, message: genericError });
                }

                const certificateTypes: Name[] = items.map((type): Name => {
                    return {
                        value: type['id'],
                        label: type['trn-name'],
                    };
                });
                return resolve({ rows: certificateTypes, pagination: newPagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const importCertificate = async (certificate: number[], password: string, siteId: string, fileSettingsType: number, translate: Translator): Promise<any> => {
    try {
        const newRecord = {
            type: "importCertificate",
            attributes: {
                siteId: siteId,
                certificateType: fileSettingsType,
                files: [{
                    fileContentBytes: certificate,
                    filePassword: password,
                    fileType:"pfx"
                }]
            }
        }
        const url = '';
        let response;

        await apiConnector.sendData(url, true, newRecord, true).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                reject(processServerError(error));
            });
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (response.ok === false) {
                    return reject({ status: response.status, message: translate.byKey(TranslationKeys.certificateImportError) });
                }
                return resolve({ id: 0, success: true });
            }
            return reject({ status: response.status, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const exportCertificate = async (id: number, name: string, password: string, certificateType: number, translate: Translator): Promise<any> => {
    try {
        const body = {
            type: "exportCertificate",
            attributes: {
                ids: [id],
                certificateType: certificateType,
                files: [{
                    filePassword: password,
                    fileName: name
                }]
            }
        };
        const url = '';
        let response;

        await apiConnector.sendData(url, true, body, true)
            .then((data) => { response = data; })
            .catch((error) => {
                return new Promise((resolve, reject) => {
                    return reject(processServerError(error));
                });
            });

        return new Promise(async (resolve, reject) => {
            if (response) {
                const content = await response.json();
                if (content.errors) {
                    return reject({ status: response.status, message: content.errors[0].detail });
                }
                if (response.ok === false) {
                    return reject({ status: response.status, message: translate.byKey(TranslationKeys.certificateImportError) });
                }
                return resolve({ data: content });
            }

            return reject({ status: response.status, success: false, message: genericError });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getValidCertificateAuthorities = async (listQuery?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const validCertAuthorityFilter = "isValidCertificateAuthority==true";
        let filters: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        filters = filters ? `${filters};${validCertAuthorityFilter}` : `&filter=${validCertAuthorityFilter}`;
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filters);
        const url = `ksCertificate?${paginationString}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            new Deserializer().deserialize(json, (err: any, apiCertificates: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const certificateAuthorities: Name[] = apiCertificates.map((cert): Name => {
                    return {
                        value: cert['id'],
                        label: cert['name'] || cert['common-name'],
                        optionalColumns: [{ key: 'commonName', value: cert['common-name'] }]
                    };
                });

                return resolve({ rows: certificateAuthorities, pagination: newPagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const importCrlCertificate = async (caLinked: boolean, files:any[] , translate: Translator,) : Promise<StoreEntityResult> => {
    try {
        const newRecord = {
            type: "importCrl",
            attributes: {
                contentType:"BYTES",
                usingLinkedCaCertificate: caLinked,
                files: files
            }
        }
        const url = '';
        let response;

        await apiConnector.sendData(url, true, newRecord, true).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                reject(processServerError(error));
            });
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                const json = await response.json();
                if (response.ok === false) {
                    if (json.errors) {
                        return reject({ status: response.status, message: json.errors[0].detail });
                    }
                    return reject({ status: response.status, message: translate.byKey(TranslationKeys.certificateImportError) });
                }
                return resolve({ id: 0, success: true });
            }
            return reject({ status: response.status, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const revokeCertificate = async (ids: number[], translate: Translator, siteId: string, updateService: boolean): Promise<StoreEntityResult> => {
    try {
        const newRecord = {
            type: "revokeCertificate",
            attributes: {
                ids: ids,
                siteId: siteId,
                updateService: updateService
            }
        }
        const url = '';
        let response;

        await apiConnector.sendData(url, true, newRecord, true).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                reject(processServerError(error));
            });
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                const json = await response.json();
                if (response.ok === false) {
                    if (json.errors) {
                        return reject({ status: response.status, message: json.errors[0].detail });
                    }
                    return reject({ status: response.status, message: translate.byKey(TranslationKeys.certificateImportError) });
                }
                return resolve({ id: 0, success: true });
            }
            return reject({ status: response.status, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const importMultipleCertificate = async (alias: string, certificates: { fileContentBytes: number[], filePassword?: string, fileType: string, fileName: string }[], translate: Translator, siteId: string, certificateType: string): Promise<any> => {
    try {
        const newRecord = {
            type: "importCertificate",
            attributes: {
                certificateAlias: alias,
                siteId: siteId,
                certificateType: certificateType,
                files: certificates
            }
        }
        const url = '';
        let response;

        await apiConnector.sendData(url, true, newRecord, true).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                reject(processServerError(error));
            });
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (response.ok === false) {
                    return reject({ status: response.status, message: translate.byKey(TranslationKeys.certificateImportError) });
                }
                return resolve({ id: 0, success: true });
            }
            return reject({ status: response.status, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};
